import Seo from "./Seo";
import LegalPage from "./LegalPage";
import RegisteredEntityBlock from "./RegisteredEntityBlock";
import { BUSINESS, organizationJsonLd } from "./business";

const H = ({ children }) => <h2 className="font-heading text-xl sm:text-2xl font-black mt-6">{children}</h2>;
const P = ({ children }) => <p className="text-sm sm:text-base font-medium text-[#111111] leading-relaxed">{children}</p>;
const Li = ({ children }) => <li className="text-sm sm:text-base font-medium text-[#111111] leading-relaxed">{children}</li>;

export default function DataDeletion() {
  const subject = encodeURIComponent("Data deletion request");
  const bodyText = encodeURIComponent(
    "Hi DueMate team,\n\nI would like my DueMate account and all associated data to be deleted.\n\n" +
    "Account email: <your DueMate login email>\n" +
    "Reason (optional): \n\nThank you."
  );
  const mailto = `mailto:${BUSINESS.email}?subject=${subject}&body=${bodyText}`;

  return (
    <>
      <Seo
        title="Data Deletion Instructions — DueMate"
        description={`How to request deletion of your DueMate account and personal data. Operated by ${BUSINESS.legalName} (Udyam ${BUSINESS.udyamNumber}).`}
        jsonLd={organizationJsonLd()}
      />
      <LegalPage title="Data Deletion Instructions" effectiveDate="1 September 2026">
        <RegisteredEntityBlock />

        <P>
          You can request deletion of your DueMate account and all personal data associated with it at any time. This page explains what will be deleted and how to submit a request. This applies to data collected during use of the DueMate SaaS application at <a className="underline text-[#4361EE]" href={BUSINESS.website}>{BUSINESS.website}</a>, including data obtained via Facebook Login or Meta WhatsApp Business Cloud API integrations.
        </P>

        <H>1. What data can be deleted</H>
        <ul className="list-disc pl-6 space-y-2">
          <Li>Your DueMate <strong>account profile</strong> (name, email, profile picture, business name and type).</Li>
          <Li>Your <strong>customer records</strong> and their service history.</Li>
          <Li>Your <strong>invoices, payments, reminders</strong> and message templates.</Li>
          <Li>Your <strong>WhatsApp message history</strong> (outbound reminders and inbound replies) processed through the Meta WhatsApp Business Cloud API in the course of using DueMate.</Li>
          <Li>Any data received from Facebook Login (basic profile) or Meta platform integrations linked to your account.</Li>
          <Li>Your <strong>session tokens</strong> and any cookies stored server-side.</Li>
        </ul>

        <H>2. How to request deletion</H>
        <P>Choose either option:</P>
        <div className="grid sm:grid-cols-2 gap-3 not-prose">
          <a
            href={mailto}
            data-testid="delete-email-link"
            style={{ background: "#4361EE", color: "#FFFFFF" }}
            className="nb-card p-4 sm:p-5 no-underline hover:brightness-110 transition"
          >
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-80">Option A — email</p>
            <p className="font-heading text-lg font-black leading-tight mt-1">Email deletion request</p>
            <p className="text-xs font-semibold mt-2 opacity-90 break-all">{BUSINESS.email}</p>
          </a>
          <div style={{ background: "#FFD166" }} className="nb-card p-4 sm:p-5">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase">Option B — in-app</p>
            <p className="font-heading text-lg font-black leading-tight mt-1">Delete from Settings</p>
            <p className="text-xs font-semibold mt-2">
              Sign in to DueMate → Settings → "Delete account". You'll be asked to confirm.
            </p>
          </div>
        </div>
        <P className="mt-4">
          If you choose email, please send from the same email address you use to sign in to DueMate and include a short confirmation like <em>"Please delete my DueMate account and all associated data."</em>
        </P>

        <H>3. What happens after we receive the request</H>
        <ul className="list-disc pl-6 space-y-2">
          <Li>We will verify the request by matching the email address to the DueMate account.</Li>
          <Li>Your account and personal data are deleted or irreversibly anonymised within <strong>30 days</strong> of verification.</Li>
          <Li>You will receive a confirmation email once deletion is complete.</Li>
          <Li>Backups may retain data for up to a further <strong>60 days</strong>, after which they are overwritten in the normal rotation cycle.</Li>
        </ul>

        <H>4. Data that may be retained</H>
        <P>To comply with tax, accounting, fraud-prevention and other legal obligations, we may retain a minimal set of records after deletion:</P>
        <ul className="list-disc pl-6 space-y-2">
          <Li>Invoices we issued to you and payment-gateway transaction references (retained as required by Indian tax law).</Li>
          <Li>Audit logs required to defend against or investigate abuse or security incidents.</Li>
          <Li>Aggregated, anonymised usage statistics that cannot be linked back to you.</Li>
        </ul>

        <H>5. Third-party providers</H>
        <P>Where your data was shared with third-party service providers as part of operating DueMate, we will:</P>
        <ul className="list-disc pl-6 space-y-2">
          <Li>Remove your account, tokens and messages from our systems immediately.</Li>
          <Li>Stop sending any further requests to Meta / Google / payment gateways on your behalf.</Li>
          <Li>Note that data already delivered to a recipient via WhatsApp (i.e., the message you sent to your customer) cannot be recalled from the recipient's phone.</Li>
        </ul>

        <H>6. Contact</H>
        <P>
          For any question about data deletion, email us at <a className="underline text-[#4361EE]" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>.
        </P>
        <P>
          See also our <a className="underline text-[#4361EE]" href="/privacy-policy">Privacy Policy</a> for full details on how DueMate collects and processes personal data.
        </P>
      </LegalPage>
    </>
  );
}
